import {getTestProps} from '@benchling/js-test-utils';
import {
  ActionIcon,
  Box,
  Card,
  Chip,
  createStyles,
  Divider,
  Grid,
  Group,
  Image,
  Radio,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import React, {useMemo} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {UseAntibodyFormatsQueryResult} from 'antibodies/hooks/useAntibodyFormatsQuery';
import {useImplementedAntibodySchemaInterfacesQuery} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import {buildAntibodySchemaFormatMaps} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationUtil';
import {ALLOWED_ANTIBODY_FORMAT_IDS} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {NdcStartingType} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import AntibodyFormatPicker, {
  type AntibodyTileProps,
} from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {assert} from 'Asserts';
import {OBJECTS_REGISTERABLE_PERMISSION} from 'auth/PermissionConstants';
import {type TagSchema} from 'constants/RegistryConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import SelectableCard from 'ui-library/inputs/SelectableCard';

const useStyles = createStyles((theme) => ({
  formatPicker: {
    h4: {
      marginBottom: theme.spacing.sm,
    },
    p: {
      marginBottom: theme.spacing.md,
    },
  },
  startingTypeSection: {
    h4: {
      marginBottom: 0,
    },
  },
  variablePairsSection: {
    marginTop: theme.spacing.lg,
  },
  tooltipIcon: {
    cursor: 'help',
    color: theme.colors.gray[6],
  },
  banner: {
    marginTop: '16px',
  },
}));

function VariablePairsTooltipLabel(): JSX.Element {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder={true} w="350px">
      <Card.Section p="xs">
        <Group spacing="md" align="flex-start" noWrap={true}>
          <Box w="90px" h="90px">
            <Image
              src="/static/img/antibody-format-variable-pairs.svg"
              alt="Example of a variable pair on an IgG Monospecific Antibody"
            />
          </Box>
          {/* We need to re-enable wrapping since Group noWrap gets inherited */}
          <Stack spacing="lg" style={{flex: 1, minWidth: 0, whiteSpace: 'normal'}}>
            <Text size="sm">
              A variable pair is a custom entity that points to both the VH and VL domain sequences
            </Text>
            <Text size="sm">
              By providing a variable pair, you don't have to input the VH and VL domains separately
            </Text>
          </Stack>
        </Group>
      </Card.Section>
    </Card>
  );
}

export interface SelectFormatPaneProps {
  selectedFormatId: string | null;
  setSelectedFormat: (format: AntibodyFormat | null) => void;
  selectedSchemaId: string | null;
  setSelectedSchemaId: (schemaId: string | null) => void;
  setRegistryId: (registryId: string | null) => void;
  startingType: NdcStartingType;
  setStartingType: (startingType: NdcStartingType) => void;
  antibodyFormatsQuery: UseAntibodyFormatsQueryResult;
}

/**
 * Nested data creation spreadsheet import modal step that allows the user to select the format of the
 * antibodies they want to create and the schema to use, and what data type they're starting from.
 */
export default function SelectFormatPane({
  selectedFormatId,
  setSelectedFormat,
  selectedSchemaId,
  setSelectedSchemaId,
  setRegistryId,
  startingType,
  setStartingType,
  antibodyFormatsQuery,
}: SelectFormatPaneProps): JSX.Element {
  const {classes} = useStyles();
  const {status: antibodyFormatsQueryStatus, antibodyFormats} = antibodyFormatsQuery;
  const {status: implementedAntibodySchemaInterfacesQueryStatus, implementedAntibodySchemaInterfaces} =
    useImplementedAntibodySchemaInterfacesQuery();

  const {schemaIdToFormat, formatIdToSchemaIds, allSchemaIds} = useMemo(
    () =>
      buildAntibodySchemaFormatMaps(
        implementedAntibodySchemaInterfaces.filter(
          (implementedInterface) => !implementedInterface.schema.archived
        )
      ),
    [implementedAntibodySchemaInterfaces]
  );

  const antibodyTileProps: Array<AntibodyTileProps> =
    antibodyFormatsQueryStatus === QueryResultType.SUCCESS
      ? antibodyFormats
          .filter(({id}) => ALLOWED_ANTIBODY_FORMAT_IDS.includes(id))
          // Only show formats that have a defined antibody schema using this format
          .filter(({id}) => formatIdToSchemaIds.has(id))
          .map(
            (format): AntibodyTileProps => ({
              format,
              showLabels: false,
              disabled: false,
            })
          )
      : [];

  function onSelectFormatId(formatId: string): void {
    assert(antibodyFormatsQueryStatus === QueryResultType.SUCCESS, 'Expected antibody formats to be loaded');
    const format = antibodyFormats.find(({id}) => id === formatId);
    assert(format != null, 'Expected format to be found in antibodyFormats');
    setSelectedFormat(format);
    if (selectedSchemaId != null) {
      assert(schemaIdToFormat.has(selectedSchemaId), 'Expected schema to be found in schemaIdToFormat');
      if (formatId !== schemaIdToFormat.get(selectedSchemaId)!.id) {
        setSelectedSchemaId(null);
        setRegistryId(null);
      }
    }
  }

  const onSelectSchema = (tagSchema: TagSchema | null): void => {
    setSelectedSchemaId(tagSchema?.id ?? null);
    setRegistryId(tagSchema?.registryId ?? null);
    if (tagSchema != null) {
      assert(schemaIdToFormat.has(tagSchema.id), 'Expected schema to be found in schemaIdToFormat');
      setSelectedFormat(schemaIdToFormat.get(tagSchema.id)!);
    }
  };

  const filteredSchemaIds =
    selectedFormatId == null
      ? new Set(
          [...allSchemaIds].filter((schemaId) =>
            ALLOWED_ANTIBODY_FORMAT_IDS.includes(schemaIdToFormat.get(schemaId)!.id)
          )
        )
      : formatIdToSchemaIds.get(selectedFormatId)!;

  return (
    <div {...getTestProps({component: 'SelectFormatPane'})}>
      {antibodyFormatsQueryStatus === QueryResultType.ERROR && (
        <div className={classes.banner}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading protein formats" />
        </div>
      )}

      {implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.ERROR && (
        <div className={classes.banner}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading protein schemas" />
        </div>
      )}

      <div className={classes.formatPicker}>
        <h4>Format</h4>
        <p>Select the format of the antibodies you want to create</p>
        <AntibodyFormatPicker
          antibodyTileProps={antibodyTileProps}
          disabled={false}
          selectedFormat={selectedFormatId}
          setSelectedFormat={onSelectFormatId}
          enableCreateNew={false}
        />
      </div>

      <Grid mt="md">
        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'schemaSelector'})}>
          <FormGroup hasError={implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.ERROR}>
            <FieldLabel label="Schema" isRequired={true} />
            {implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.PENDING ? (
              <div>
                <HorizontalThrobber />
              </div>
            ) : (
              <TagSchemaSelector
                selectedTagSchemaId={selectedSchemaId}
                onChange={onSelectSchema}
                tagSchemaFilterFn={(tagSchema) => filteredSchemaIds.has(tagSchema.id)}
                shouldDefaultIfSingleItem={true}
                requiredPermissions={[OBJECTS_REGISTERABLE_PERMISSION]}
              />
            )}
          </FormGroup>
        </Grid.Col>
      </Grid>

      <Divider my="xl" />

      <div className={classes.startingTypeSection}>
        <Title order={5} fw={600}>
          Starting antibody component
        </Title>
        <Text size="sm" mb="lg">
          Select the starting component to start creating antibodies
        </Text>
        <Chip.Group
          // We map PAIRS to DOMAINS since this selector must be set to DOMAINS for the Variable Pairs section
          // below to be shown. The selector below handles setting the starting type to PAIRS.
          value={startingType === NdcStartingType.PAIRS ? NdcStartingType.DOMAINS : startingType}
          onChange={(value) => {
            setStartingType(value as NdcStartingType);
          }}
          multiple={false}
        >
          <Grid gutter="md">
            <Grid.Col span={6}>
              <SelectableCard
                value={NdcStartingType.DOMAINS}
                title="Domains"
                description="Provide domain sequences that are combined to create chains"
                icon={
                  <Box w="60px" h="60px">
                    <Image
                      src="/static/img/antibody-format-domains.svg"
                      alt="Example of domains on an IgG Monospecific Antibody"
                    />
                  </Box>
                }
                dataTestAttrs={{component: 'SelectFormatPane', element: 'domainsStartingTypeCard'}}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <SelectableCard
                value={NdcStartingType.CHAINS}
                title="Chains"
                description="Provide chain sequences that are broken into component domains"
                icon={
                  <Box w="60px" h="60px">
                    <Image
                      src="/static/img/antibody-format-chains.svg"
                      alt="Example of chains on an IgG Monospecific Antibody"
                    />
                  </Box>
                }
                dataTestAttrs={{component: 'SelectFormatPane', element: 'chainsStartingTypeCard'}}
              />
            </Grid.Col>
          </Grid>
        </Chip.Group>

        {(startingType === NdcStartingType.DOMAINS || startingType === NdcStartingType.PAIRS) && (
          <div
            className={classes.variablePairsSection}
            {...getTestProps({component: 'SelectFormatPane', element: 'variablePairsSection'})}
          >
            <Radio.Group
              name="startingTypeVariablePairs"
              label="Are you using variable pairs as inputs?"
              description={
                <Group spacing="xs" align="center">
                  <Tooltip
                    label={<VariablePairsTooltipLabel />}
                    styles={{
                      tooltip: {
                        backgroundColor: 'white',
                        borderRadius: '3px',
                        padding: 0,
                      },
                    }}
                  >
                    <ActionIcon aria-label="Info Tooltip" variant="transparent" size="12px" p={0}>
                      <BenchlingIcon className={classes.tooltipIcon} icon="info-circle" />
                    </ActionIcon>
                  </Tooltip>
                  <Text mb={0} color="black">
                    What is a variable pair?
                  </Text>
                </Group>
              }
              value={startingType === NdcStartingType.PAIRS ? 'yes' : 'no'}
              onChange={(value) => {
                setStartingType(value === 'yes' ? NdcStartingType.PAIRS : NdcStartingType.DOMAINS);
              }}
              styles={{
                label: {
                  fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
                  color: 'black',
                  marginBottom: '4px',
                },
                description: {
                  '& p': {
                    fontWeight: 'normal',
                    color: 'black',
                  },
                },
              }}
            >
              <Group mt="lg">
                <Radio value="yes" label="Yes" />
                <Radio value="no" label="No" />
              </Group>
            </Radio.Group>
          </div>
        )}
      </div>
    </div>
  );
}
